import { onMounted, ref } from 'vue'
import type { Employee } from '~/models/graphql/generated/types'

let cachedEmployees: Employee[] | null = null
let pendingFetch: Promise<Employee[]> | null = null

export function resetEmployeesCacheForTests() {
  cachedEmployees = null
  pendingFetch = null
}

export async function fetchEmployees(): Promise<Employee[]> {
  if (cachedEmployees) {
    return cachedEmployees
  }
  if (!pendingFetch) {
    pendingFetch = useBffClient()
      .getEmployees()
      .then((data) => {
        cachedEmployees = data
        return data
      })
      .catch((err) => {
        pendingFetch = null
        throw err
      })
  }
  return pendingFetch
}

export function useEmployees() {
  const employees = ref<Employee[]>(cachedEmployees ?? [])
  const loading = ref(!cachedEmployees)
  const error = ref<unknown>(null)

  async function load(force = false) {
    if (force) {
      cachedEmployees = null
      pendingFetch = null
    }
    if (cachedEmployees && !force) {
      employees.value = cachedEmployees
      loading.value = false
      return cachedEmployees
    }
    loading.value = true
    error.value = null
    try {
      const data = await fetchEmployees()
      employees.value = data
      return data
    } catch (e) {
      error.value = e
      console.error('Failed to load employees', e)
      throw e
    } finally {
      loading.value = false
    }
  }

  onMounted(() => {
    void load()
  })

  return {
    employees,
    loading,
    error,
    load,
    refresh: () => load(true),
  }
}
